import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFreshdeskContextState, useNativeObjectsState } from 'store';
import useSchemaPermissions from './use-schema-permissions';
import useRecordPermissions from './use-record-permissions';
import { entityPageController } from 'controllers';
import { ENTITY_FIELD_TYPE, LEGO_CONSTANTS, SCHEMA_TYPE } from 'constants/index';
import useRecordsPage from './use-records-page';
import useMessageChannelAdapter from './use-message-channel-adapter';
import useIsRecordsColumnVisible from './use-is-records-column-visible';
import nativeObjectConfigs from 'configs/nativeObjectConfigs';
import { getNativeObjectIDForConfigType } from 'components/RecordPage/RecordDetails/Widgets/utils/util';

const useEntityPage = () => {
  const { t: translationFn } = useTranslation();
  const nativeObjectData = useNativeObjectsState();
  const { getUserDetails, getConfigType } = useFreshdeskContextState();
  const { getRecordsLookupById } = useRecordsPage();
  const { resolveFieldValues } = useMessageChannelAdapter();
  const language = getUserDetails()?.locale?.language;
  const timezone = getUserDetails()?.locale?.timezone;
  const configType = getConfigType();
  const isRecordsColumnVisible = useIsRecordsColumnVisible();
  const showRecordId = isRecordsColumnVisible('recordId');
  const { permissions: schemaPermissions } = useSchemaPermissions();
  const { permissions: recordPermissions } = useRecordPermissions();
  entityPageController.setPermissions(schemaPermissions, recordPermissions, configType);

  const prepareRowsForPlatformTable = useCallback(
    async (configType, entityState, recordsList, filterQuery, translationDataForEntities = [], nativeDropdownChoicesByFieldId = new Map()) => {
      const { entity, isLoading } = entityState;
      if (!translationFn || !entity || isLoading || !nativeObjectData.loaded) return [];

      const nativeIdLookup = {};
      const nativeFieldNamesLookupToId = entityPageController.getNativeRelationshipFieldNameToIdLookup(entity) ?? {};
      const nativeFieldNames = Object.keys(nativeFieldNamesLookupToId);
      for (const index in recordsList) {
        const record = recordsList[index];
        Object.keys(record.data)
          .filter(fieldName => nativeFieldNames.includes(fieldName))
          .forEach(fieldName => {
            const nativeSchemaId = nativeFieldNamesLookupToId[fieldName];
            nativeIdLookup[nativeSchemaId] = nativeIdLookup[nativeSchemaId] ?? [];
            if (record.data[fieldName]) {
              nativeIdLookup[nativeSchemaId].push(record.data[fieldName]);
            }
          });
      }
      // Split legacy native-schema ids (ticket/contact/company, resolved below via the existing message-channel
      // mechanism) from datasource native_lookup ids (resolved via the field-resolutions API) - the latter is
      // only ever populated under useTypeBasedApi. This also fixes a crash: nativeObjectData.idLookup[nativeSchemaId]
      // is undefined for datasource ids, so accessing `.context` on it directly (as before) would throw.
      const { legacyIdsByContext, datasourceIdsById } = entityPageController.partitionNativeFieldIds(nativeIdLookup, nativeObjectData);

      const nativeAssociations = {};
      for (const context of Object.keys(legacyIdsByContext)) {
        nativeAssociations[context] = getRecordsLookupById(context, legacyIdsByContext[context]);
      }
      const associationResponseArray = await Promise.allSettled(Object.values(nativeAssociations));
      const nativeAssociationLookup = associationResponseArray
        .filter(res => res.status === 'fulfilled')
        .map(res => res.value)
        .reduce((map, item) => {
          return { ...map, [item.context]: item.response };
        }, {});

      let nativeLookupResolutionsByRef = new Map();
      if (Object.keys(datasourceIdsById).length) {
        const resolution = await resolveFieldValues(entityPageController.buildFieldResolutionsPayload(datasourceIdsById));
        if (resolution.success) nativeLookupResolutionsByRef = entityPageController.mapFieldResolutionsResponse(resolution.response);
      }

      const enrichedRecords = {};
      for (const index in recordsList) {
        const record = recordsList[index];
        const { enrichedRecordData } = entityPageController.getEnrichRecordData(entity, record, nativeObjectData, translationFn, language, timezone, {
          configType,
          showRecordId,
          choicesByFieldId: nativeDropdownChoicesByFieldId,
          nativeLookupResolutionsByRef,
        });
        for (const data of enrichedRecordData) {
          if (
            data.fieldType === ENTITY_FIELD_TYPE.RELATIONSHIP &&
            data.fieldSubType === SCHEMA_TYPE.NATIVE &&
            data.displayValue?.meta?.source !== 'datasource' && // already resolved inline above
            data.displayValue.value &&
            data.displayValue.value !== LEGO_CONSTANTS.EMPTY_DEFAULT_DATA
          ) {
            const nativeRecordLookupById = nativeAssociationLookup[data.displayValue?.meta];
            const configs = nativeObjectConfigs[configType][data.displayValue?.meta];
            const displayValueToBeConverted = getNativeObjectIDForConfigType(configType, data.displayValue.value);
            const displayDataForNativeContext =
              nativeRecordLookupById?.[displayValueToBeConverted]?.[configs.defaultDisplayField] ??
              nativeRecordLookupById?.[data.displayValue?.value]?.[configs.defaultDisplayField];
            data.displayValue.value = displayDataForNativeContext ?? LEGO_CONSTANTS.EMPTY_DEFAULT_DATA;
          }
        }
        enrichedRecords[record.display_id] = enrichedRecordData;
      }
      return entityPageController.__constructRows({
        entity,
        enrichedRecords,
        translationFn,
        primaryFieldData: filterQuery,
        translationDataForEntities,
        showRecordId,
      });
    },
    [language, nativeObjectData, timezone, translationFn, getRecordsLookupById, showRecordId, resolveFieldValues]
  );

  return useMemo(() => {
    return {
      prepareRowsForPlatformTable,
    };
  }, [prepareRowsForPlatformTable]);
};

export default useEntityPage;
